import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ant design icons
import DisconnectOutlined from '@ant-design/icons/DisconnectOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import CalendarOutlined from '@ant-design/icons/CalendarOutlined';

// project imports
import MainCard from 'components/MainCard';
import useOnlineStatus from 'hooks/useOnlineStatus';
import useAuth from 'hooks/useAuth';
import { getCachedChildren, getCachedSessions, getCachedChildrenForTherapist, getCachedSessionsForTherapist } from 'utils/offlineDB';

// Shared Calendar component
import TherapistCalendar from 'pages/therapist/calendar';

// Offline profile picker
import OfflineProfilePicker from 'pages/offline/OfflineProfilePicker';

// ==============================|| OFFLINE CALENDAR PAGE ||============================== //

const OfflineCalendar = () => {
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const { isOnline, isOfflineReady, offlineUser, activeOfflineUser } = useOnlineStatus();
  // Authenticated user is the source of truth: a logged-in therapist must never
  // be sent to the profile picker when the connection drops.
  const { user: authUser, isLoggedIn } = useAuth();

  const [cachedChildren, setCachedChildren] = useState([]);
  const [cachedSessions, setCachedSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Determine effective person ID from the logged-in user, an offline session,
  // or a profile-picker selection (in that order of trust).
  const effectivePersonId = activeOfflineUser?.personId || offlineUser?.person_id || offlineUser?.id || authUser?.person_id || authUser?.id;

  // Load cached data on mount or when effective user changes
  useEffect(() => {
    const loadCachedData = async () => {
      try {
        let children, sessions;
        if (effectivePersonId) {
          // Strictly therapist-scoped: each profile sees ONLY its own calendar.
          // No cross-therapist fallback — falling back to the global cache would
          // show one therapist another therapist's sessions/children.
          children = await getCachedChildrenForTherapist(effectivePersonId);
          sessions = await getCachedSessionsForTherapist(effectivePersonId);
        } else {
          children = await getCachedChildren();
          sessions = await getCachedSessions();
        }
        setCachedChildren(children);
        setCachedSessions(sessions);
      } catch (error) {
        console.error('Error loading cached data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCachedData();
  }, [effectivePersonId]);

  // Show the profile picker ONLY when offline AND nobody is authenticated AND no
  // offline session / picked profile exists. A logged-in therapist stays on the
  // (offline) calendar instead of being asked "who's using this device?".
  if (!isOnline && !isLoggedIn && !offlineUser && !activeOfflineUser) {
    return <OfflineProfilePicker />;
  }

  // If loading, show loading state
  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>{intl.formatMessage({ id: 'offline.calendar.loading' })}</Typography>
      </Box>
    );
  }

  // If not offline ready (user hasn't logged in with internet at least once)
  if (!isOfflineReady && cachedSessions.length === 0) {
    return (
      <MainCard>
        <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
          <DisconnectOutlined style={{ fontSize: '4rem', color: theme.palette.warning.main }} />
          <Typography variant="h4" textAlign="center">
            {intl.formatMessage({ id: 'offline.calendar.notAvailableTitle' })}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 500 }}>
            {intl.formatMessage({ id: 'offline.calendar.notAvailableDescription' })}
          </Typography>
          {isOnline && (
            <Button variant="contained" onClick={() => navigate('/login')} startIcon={<UserOutlined />}>
              {intl.formatMessage({ id: 'offline.calendar.goToLogin' })}
            </Button>
          )}
        </Stack>
      </MainCard>
    );
  }

  // If no sessions cached
  if (cachedSessions.length === 0) {
    return (
      <MainCard>
        <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
          <CalendarOutlined style={{ fontSize: '4rem', color: theme.palette.grey[400] }} />
          <Typography variant="h4" textAlign="center">
            {intl.formatMessage({ id: 'offline.calendar.noSessions' })}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 500 }}>
            {intl.formatMessage({ id: 'offline.calendar.noSessionsDescription' })}
          </Typography>
          {isOnline && (
            <Button variant="contained" onClick={() => navigate('/login')} startIcon={<CalendarOutlined />}>
              {intl.formatMessage({ id: 'offline.calendar.loginSyncCalendar' })}
            </Button>
          )}
        </Stack>
      </MainCard>
    );
  }

  // Render the shared Calendar component with offline props
  return <TherapistCalendar isOfflineMode={true} offlineSessions={cachedSessions} offlineChildren={cachedChildren} />;
};

export default OfflineCalendar;
